import React, { useEffect, useState } from 'react';
import { TextField, InputAdornment } from '@mui/material';

import SectionCard from './SectionCard';
import { Fact, FactGrid } from './sectionParts';
import { formatMoneyFull } from '../../reports/finance/chartConfig';
import { useAuth } from '../../../context/AuthContext';
import { AdmissionCycle } from '../../../types/admissions';

interface Props {
  cycle: AdmissionCycle;
  canEdit: boolean;
  onSave: (patch: Partial<AdmissionCycle>) => Promise<void>;
}

/**
 * The fee charged when a family applies. Zero makes applying free. Can be
 * changed while the cycle is open — it applies to applications from then
 * on; a challan already issued keeps its amount.
 */
const ApplicationFeeSection: React.FC<Props> = ({ cycle, canEdit, onSave }) => {
  const { currency } = useAuth();
  const current = cycle.applicationFeeAmount ?? 0;
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [amount, setAmount] = useState(String(current));

  useEffect(() => { if (editing) setAmount(String(current)); }, [editing, current]);

  const n = Number(amount);
  const valid = amount !== '' && Number.isFinite(n) && n >= 0;

  const save = async () => {
    setSaving(true);
    try {
      await onSave({ applicationFeeAmount: n });
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <SectionCard
      title="Application fee"
      description="Charged when a family applies, before the application is reviewed. Set it to 0 to make applying free."
      canEdit={canEdit}
      editing={editing}
      onEditToggle={setEditing}
      onSave={save}
      onCancel={() => setAmount(String(current))}
      saving={saving}
      canSave={valid}
    >
      {editing ? (
        <TextField
          size="small"
          label="Application fee"
          value={amount}
          onChange={(e) => setAmount(e.target.value.replace(/[^\d]/g, ''))}
          error={!valid}
          helperText={valid
            ? (cycle.status === 'Open' ? 'Applies to applications from now on. Challans already issued keep their amount.' : ' ')
            : 'Enter an amount, or 0 for free'}
          InputProps={{ startAdornment: <InputAdornment position="start">{currency}</InputAdornment> }}
          inputProps={{ inputMode: 'numeric' }}
          sx={{ maxWidth: 280 }}
        />
      ) : (
        <FactGrid columns={2}>
          <Fact label="Application fee">{current > 0 ? formatMoneyFull(current, currency) : 'Free'}</Fact>
          <Fact label="Paid">{current > 0 ? 'Before the application is reviewed' : 'Nothing to pay'}</Fact>
        </FactGrid>
      )}
    </SectionCard>
  );
};

export default ApplicationFeeSection;
